import useChangeEvent from '@dyl/hooks/useChangeEvent';
import useTaskField from '@dyl/pages/Tasks/edit/useTaskField';
import { Badge, FilledInput, Tooltip } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { findLastIndex, partial } from 'lodash';
import React, { useCallback, useMemo, useRef } from 'react';
import Draggable from 'react-draggable';

/**
 * @template T
 * @typedef {import('@dyl/pages/Tasks/edit/useTaskField').UseTaskField<T>} UseTaskField
 */

const useStyles = makeStyles(theme => ({
    card: {
        display: 'flex',
        alignItems: 'center',
        width: 56,
        aspectRatio: 1,

        borderStyle: 'solid',
        borderColor: ({ isCorrect }) => (
            isCorrect ? theme.palette.success.main
            : theme.palette.error.main
        ),

        '& input': {
            textAlign: 'center',
            padding: 0,
        },
    },

    dividerOuter: {
        width: 2,
        overflow: 'visible',
        position: 'relative',
        left: 2,
        alignSelf: 'stretch',
        marginLeft: '-8px',
        zIndex: 30,
    },
    dividerInner: {
        position: 'absolute',
        top: -8,
        bottom: -8,
        borderWidth: 2,
        borderStyle: 'solid',
        borderColor: theme.palette.grey[500],
        cursor: 'ew-resize',
    }
}));

export default function AnswerInput({ index, itemIndex }) {
    const useField = partial(useTaskField, 'items', itemIndex, 'answers', index);
    /** @type {UseTaskField<Answer[]>} */
    const [answers, setAnswers] = useTaskField('items', itemIndex, 'answers');
    const answerCount = answers.length;

    const [text, setText, textError] = useField('text');
    const onTextChange = useChangeEvent(setText);

    const [isCorrect] = useField('is_correct_answer');

    const isFirstDistractor = useMemo(() => {
        return answers.findIndex(a => !a.is_correct_answer) === index;
    }, [answers, index]);

    const isLastCorrect = useMemo(() => {
        return findLastIndex(answers, a => a.is_correct_answer) === index;
    }, [answers, index]);

    const inputRef = useRef();

    const classes = useStyles({
        isCorrect,
        isFirstDistractor,
        isLastCorrect,
    });

    const boxSize = 56 + 8;

    const onDividerDragEnd = useCallback(
        /** @type {import('react-draggable').DraggableProps['onStop']} */
        (event, data) => {
            const offset = Math.round(data.lastX / boxSize);
            if (offset === 0) return;

            setAnswers(prev => prev.map((a, i) => ({
                ...a,
                is_correct_answer: i <= index + offset,
            })));
        }, [boxSize, index]
    );

    const dividerDragBounds = useMemo(() => ({
        top: 0,
        bottom: 0,
        left: -index * boxSize,
        right: (answerCount - index - 1) * boxSize,
    }), [boxSize, index, answerCount]);

    return (
        <>
            <Tooltip title={textError || ''}>
                <Badge badgeContent={textError ? '!' : null} color='error'>
                    <FilledInput
                        className={classes.card}
                        value={text}
                        onChange={onTextChange}
                        inputRef={inputRef}
                    />
                </Badge>
            </Tooltip>

            {isLastCorrect && (
                <Draggable
                    axis='x'
                    position={{ x: 0, y: 0 }}
                    onStop={onDividerDragEnd}
                    bounds={dividerDragBounds}
                >
                    <div className={classes.dividerOuter}>
                        <div className={classes.dividerInner} />
                    </div>
                </Draggable>
            )}
        </>
    );
}